// Gemini Live API client for vAI voice mode.
//
// Flow: fetch a short-lived token from our backend (POST /api/voice/live-token),
// fetch the full curated profile (GET /api/voice/context), then open a
// WebSocket straight to Google's Live API with the profile front-loaded in
// the system instruction. The long-lived Gemini API key never leaves the
// server.
//
// The whole index is ~10k tokens — under 8% of the Live model's 128k input
// window — so every answer is grounded with no per-question tool round trip.

export const LIVE_WS_URL =
  'wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1alpha.GenerativeService.BidiGenerateContentConstrained';
export const LIVE_MODEL = 'models/gemini-3.8-live';
export const LIVE_VOICE = 'Fenrir';

export const VAI_SYSTEM_INSTRUCTION = `You are vAI, the voice of Vaibhav Kaul's personal website. You speak as Vaibhav's AI assistant, in first person, warm and direct. Keep every spoken reply short — one to three sentences — because this is voice, not text.

Below is Vaibhav's full curated profile. It is your only source of facts about him — his career, projects, and background. Answer from it directly; never invent details. If something isn't in the profile, say you don't have that on hand.

Stay factual and understated. Don't boast on his behalf.

Pronunciation: his first name is pronounced "VAI-bhav" — rhymes with "high"
plus "uv", with the stress on the first syllable. Say it that way when you
say his name aloud.

--- VAIBHAV'S PROFILE ---
`;

function pcm16ToBase64(samples) {
  const bytes = new Uint8Array(samples.buffer, samples.byteOffset, samples.byteLength);
  let s = '';
  const CHUNK = 8192;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    s += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(s);
}

function base64ToPcm16(b64) {
  const s = atob(b64);
  const bytes = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) bytes[i] = s.charCodeAt(i);
  return new Int16Array(bytes.buffer);
}

/** Resample mono float32 audio to 16kHz 16-bit PCM. */
function toPcm16k(input, inRate) {
  const outRate = 16000;
  const ratio = inRate / outRate;
  const outLen = Math.floor(input.length / ratio);
  const out = new Int16Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const pos = i * ratio;
    const i0 = Math.floor(pos);
    const i1 = Math.min(i0 + 1, input.length - 1);
    const frac = pos - i0;
    const v = input[i0] * (1 - frac) + input[i1] * frac;
    out[i] = Math.max(-32768, Math.min(32767, Math.round(v * 32767)));
  }
  return out;
}

export class VaiLiveSession {
  constructor(apiBase, events, opts) {
    this.apiBase = apiBase;
    this.events = events;
    opts = opts || {};
    this.systemInstruction = opts.systemInstruction || VAI_SYSTEM_INSTRUCTION;
    this.opener =
      opts.opener ||
      '(The session has just started. Greet the visitor briefly as vAI, ' +
        "Vaibhav's voice assistant, and ask what they'd like to know.)";
    this.voiceName = opts.voiceName || LIVE_VOICE;
    this.ws = null;
    this.micStream = null;
    this.micCtx = null;
    this.micProc = null;
    this.playCtx = null;
    this.playTime = 0;
    this.stopped = false;
    this.setupTimer = null;
    this.micChunkCount = 0;
    this.sessionId = Math.random().toString(36).slice(2, 8);
  }

  log() {
    console.log.apply(
      console,
      ['[vai-live:' + this.sessionId + ']'].concat([].slice.call(arguments))
    );
  }

  warn() {
    console.warn.apply(
      console,
      ['[vai-live:' + this.sessionId + ']'].concat([].slice.call(arguments))
    );
  }

  async start() {
    this.stopped = false;
    const ev = this.events;
    this.log('session starting');
    try {
      ev.onStatus('requesting-mic');
      this.micStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });

      ev.onStatus('connecting');
      const res = await fetch(this.apiBase + '/api/voice/live-token', {
        method: 'POST',
      });
      if (!res.ok) throw new Error('token request failed (' + res.status + ')');
      const body = await res.json();
      if (!body.token) throw new Error('no token in response');
      this.log('token minted, fetching profile context');

      const ctxRes = await fetch(this.apiBase + '/api/voice/context');
      if (!ctxRes.ok)
        throw new Error('context request failed (' + ctxRes.status + ')');
      const ctxBody = await ctxRes.json();
      if (!ctxBody.context) throw new Error('no context in response');
      this.fullSystemInstruction =
        this.systemInstruction + '\n' + ctxBody.context;
      this.log(
        'profile context loaded (' + ctxBody.context.length + ' chars)'
      );
      this.log('opening websocket');

      this.playCtx = new AudioContext({ sampleRate: 24000 });
      this.playTime = this.playCtx.currentTime;

      const ws = new WebSocket(
        LIVE_WS_URL + '?access_token=' + encodeURIComponent(body.token)
      );
      this.ws = ws;
      // Google sends setupComplete, audio, and transcripts as BINARY frames.
      ws.binaryType = 'arraybuffer';

      ws.onopen = () => {
        ws.send(
          JSON.stringify({
            setup: {
              model: LIVE_MODEL,
              generationConfig: {
                responseModalities: ['AUDIO'],
                speechConfig: {
                  voiceConfig: {
                    prebuiltVoiceConfig: { voiceName: this.voiceName },
                  },
                },
              },
              systemInstruction: {
                parts: [{ text: this.fullSystemInstruction }],
              },
              inputAudioTranscription: {},
              outputAudioTranscription: {},
            },
          })
        );
        this.setupTimer = window.setTimeout(() => {
          if (!this.stopped && this.ws === ws) {
            ev.onStatus(
              'error',
              'vAI did not answer. The voice service may be busy — try again.'
            );
            this.stop();
          }
        }, 20000);
      };

      ws.onmessage = (m) => {
        this.decodeMessage(m.data);
      };
      ws.onerror = () => {
        if (!this.stopped) ev.onStatus('error', 'Connection error. Try again.');
        this.cleanup();
      };
      ws.onclose = (e) => {
        this.warn('websocket closed', {
          code: e.code,
          reason: e.reason,
          wasClean: e.wasClean,
          stopped: this.stopped,
        });
        if (!this.stopped) {
          ev.onStatus(
            'ended',
            'The voice connection closed (code ' +
              e.code +
              (e.reason ? ': ' + e.reason : '') +
              ')'
          );
        }
        this.cleanup();
      };
    } catch (err) {
      ev.onStatus(
        'error',
        err instanceof Error ? err.message : 'Could not start live voice.'
      );
      this.cleanup();
    }
  }

  stop() {
    this.stopped = true;
    try {
      if (this.ws) this.ws.close();
    } catch (e) {
      /* already closed */
    }
    this.events.onStatus('ended');
    this.cleanup();
  }

  cleanup() {
    if (this.setupTimer !== null) {
      window.clearTimeout(this.setupTimer);
      this.setupTimer = null;
    }
    if (this.micProc) this.micProc.disconnect();
    this.micProc = null;
    if (this.micCtx) this.micCtx.close().catch(function () {});
    this.micCtx = null;
    // The output context MUST be closed too — otherwise already-buffered
    // audio keeps playing after the session is stopped.
    if (this.playCtx) this.playCtx.close().catch(function () {});
    this.playCtx = null;
    if (this.micStream) this.micStream.getTracks().forEach((t) => t.stop());
    this.micStream = null;
    this.ws = null;
  }

  startMic() {
    if (!this.micStream || !this.ws) return;
    const AC =
      window.AudioContext ||
      window.webkitAudioContext;
    const micCtx = new AC();
    this.micCtx = micCtx;
    if (micCtx.state === 'suspended') {
      micCtx.resume();
    }
    const src = micCtx.createMediaStreamSource(this.micStream);
    const proc = micCtx.createScriptProcessor(2048, 1, 1);
    this.micProc = proc;
    const ws = this.ws;
    const self = this;
    let dropWarned = false;
    proc.onaudioprocess = (e) => {
      if (self.stopped || ws.readyState !== WebSocket.OPEN) {
        if (!dropWarned) {
          dropWarned = true;
          self.warn(
            'dropping mic audio: stopped=' +
              self.stopped +
              ' wsState=' +
              ws.readyState
          );
        }
        return;
      }
      const input = e.inputBuffer.getChannelData(0);
      self.micChunkCount += 1;
      // Heartbeat so we can see the mic pipeline is alive (every ~8s).
      if (self.micChunkCount % 200 === 1) {
        let rms = 0;
        for (let i = 0; i < input.length; i += 8) rms += input[i] * input[i];
        rms = Math.sqrt(rms / (input.length / 8));
        self.log(
          'mic streaming chunk #' + self.micChunkCount + ' rms=' + rms.toFixed(4)
        );
      }
      const pcm = toPcm16k(input, micCtx.sampleRate);
      ws.send(
        JSON.stringify({
          realtimeInput: {
            audio: { data: pcm16ToBase64(pcm), mimeType: 'audio/pcm;rate=16000' },
          },
        })
      );
    };
    src.connect(proc);
    proc.connect(micCtx.destination);
  }

  playAudio(b64) {
    if (!this.playCtx || this.stopped) return;
    const samples = base64ToPcm16(b64);
    const buf = this.playCtx.createBuffer(1, samples.length, 24000);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < samples.length; i++) ch[i] = samples[i] / 32768;
    const src = this.playCtx.createBufferSource();
    src.buffer = buf;
    src.connect(this.playCtx.destination);
    const t = Math.max(this.playCtx.currentTime + 0.02, this.playTime);
    src.start(t);
    this.playTime = t + buf.duration;
  }

  /** Decode a text or binary frame, then handle it as JSON text. */
  decodeMessage(data) {
    if (typeof data === 'string') {
      this.handleMessage(data);
    } else if (data instanceof ArrayBuffer) {
      this.handleMessage(new TextDecoder().decode(data));
    } else if (typeof Blob !== 'undefined' && data instanceof Blob) {
      const self = this;
      data.text().then((t) => self.handleMessage(t));
    }
  }

  handleMessage(raw) {
    const ev = this.events;
    let msg;
    try {
      msg = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (msg.error) {
      if (this.setupTimer !== null) {
        window.clearTimeout(this.setupTimer);
        this.setupTimer = null;
      }
      ev.onStatus(
        'error',
        'Voice service error' +
          (msg.error.code ? ' (' + msg.error.code + ')' : '') +
          ': ' +
          (msg.error.message || 'unknown')
      );
      return;
    }
    if ('setupComplete' in msg) {
      if (this.setupTimer !== null) {
        window.clearTimeout(this.setupTimer);
        this.setupTimer = null;
      }
      this.log('setup complete, mic starting, opener sending');
      ev.onStatus('live');
      this.startMic();
      const opener = this.opener;
      if (this.ws) {
        this.ws.send(
          JSON.stringify({
            clientContent: {
              turns: [{ role: 'user', parts: [{ text: opener }] }],
              turnComplete: true,
            },
          })
        );
      }
      return;
    }
    const sc = msg.serverContent;
    if (!sc) return;

    if (sc.inputTranscription && sc.inputTranscription.text) {
      this.log('server heard user:', sc.inputTranscription.text);
      ev.onInputTranscript(sc.inputTranscription.text);
    }
    if (sc.outputTranscription && sc.outputTranscription.text) {
      this.log('model transcript:', sc.outputTranscription.text);
      ev.onOutputTranscript(sc.outputTranscription.text);
    }

    const parts =
      sc.modelTurn && sc.modelTurn.parts ? sc.modelTurn.parts : [];
    let spoke = false;
    for (const part of parts) {
      const d = part.inlineData;
      if (d && d.data && d.mimeType && d.mimeType.indexOf('audio/pcm') === 0) {
        this.playAudio(d.data);
        spoke = true;
      }
    }
    if (spoke) ev.onStatus('speaking');
    if (sc.interrupted) {
      // The model was cut off mid-turn (on a phone this is often its own
      // voice echoing back through the mic). An interrupted turn is a
      // fragment, not a reply, so it gets its own event: the UI drops the
      // partial text instead of finalizing it into the transcript (which is
      // what made one reply show up twice). Reset the playback clock too, so
      // the restarted reply's audio doesn't queue behind stale timing.
      if (this.playCtx) this.playTime = this.playCtx.currentTime;
      ev.onStatus('live');
      if (ev.onInterrupted) ev.onInterrupted();
    } else if (sc.turnComplete) {
      ev.onStatus('live');
      ev.onTurnComplete();
    }
  }
}
